import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Zap, Eye, EyeOff, ArrowRight, Loader } from 'lucide-react'
import { useAuth } from '@/platform/auth/useAuth'
import { ApiError } from '@/shared/types/api.types'
import { Footer } from '@/platform/shell/Footer'

import styles from './AuthPages.module.css'

export function RegisterPage() {
  const { register } = useAuth()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)

  const passwordStrength = password.length === 0
    ? null
    : password.length < 8
    ? 'weak'
    : password.length < 12
    ? 'fair'
    : 'strong'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setFieldErrors({})
    setIsLoading(true)

    try {
      await register(email, password, name)
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isValidationError && err.fieldErrors) {
          const errs: Record<string, string> = {}
          err.fieldErrors.forEach(fe => { errs[fe.field] = fe.message })
          setFieldErrors(errs)
        } else {
          setError(err.message)
        }
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'var(--color-bg)' }}>
      <div className={styles.page} style={{ flex: '1 0 auto', minHeight: 'auto' }}>
        <div className={styles.bg} aria-hidden="true">
          <div className={styles.bgGlow} />
        </div>

        <div className={styles.card}>
          <div className={styles.brand}>
            <div className={styles.logo}>
              <Zap size={22} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className={styles.brandName}>NTTD</h1>
              <p className={styles.brandTagline}>No Time To Delay</p>
            </div>
          </div>

          <div className={styles.header}>
            <h2 className={styles.title}>Create your account</h2>
            <p className={styles.subtitle}>Start your execution journey today</p>
          </div>

          {error && (
            <div className={styles.errorBanner} role="alert">{error}</div>
          )}

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.field}>
              <label htmlFor="register-name" className={styles.label}>Full name</label>
              <input
                id="register-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                required
                disabled={isLoading}
              />
              {fieldErrors.name && <span className={styles.fieldError}>{fieldErrors.name}</span>}
            </div>

            <div className={styles.field}>
              <label htmlFor="register-email" className={styles.label}>Email</label>
              <input
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={isLoading}
              />
              {fieldErrors.email && <span className={styles.fieldError}>{fieldErrors.email}</span>}
            </div>

            <div className={styles.field}>
              <label htmlFor="register-password" className={styles.label}>Password</label>
              <div className={styles.passwordWrapper}>
                <input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordStrength && (
                <div className={styles.strengthBar}>
                  <div className={`${styles.strengthFill} ${styles[`strength_${passwordStrength}`]}`} />
                  <span className={styles.strengthLabel}>{passwordStrength}</span>
                </div>
              )}
              {fieldErrors.password && <span className={styles.fieldError}>{fieldErrors.password}</span>}
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isLoading || !email || !password || !name}
              id="register-submit"
            >
              {isLoading ? (
                <><Loader size={16} className="animate-spin" /> Creating account…</>
              ) : (
                <>Create account <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className={styles.footerText}>
            Already have an account?{' '}
            <Link to="/login" className={styles.footerLink}>Sign in</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  )
}
